import {useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent} from 'react';
import {Compass, Maximize2, Minimize2, RotateCcw, ZoomIn, ZoomOut} from 'lucide-react';
import {createRenderer, type PanoRenderer, type PanoView} from './renderer';

const MIN_FOV = 0.62; // ≈ 36°, zoomed in
const MAX_FOV = 1.85; // ≈ 106°, wide
const MAX_PITCH = (85 * Math.PI) / 180;
const AUTO_SPEED = 0.045; // radians per second, barely moving

function clamp(value: number, low: number, high: number): number {
  return value < low ? low : value > high ? high : value;
}

/**
 * The 360 viewer.
 *
 * Interaction rules it follows, all of them because a listing is browsed with a
 * thumb as often as with a mouse:
 *   · one finger looks around, two fingers zoom, the wheel zooms;
 *   · it drifts gently until the visitor touches it, then never again — motion
 *     is an invitation, not a carousel;
 *   · every gesture has a button beside it (zoom, recentre, full screen), and
 *     `prefers-reduced-motion` removes the drift entirely.
 */
export function Pano360({label, src}: {label?: string; src: string}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<PanoRenderer | null>(null);
  const viewRef = useRef<PanoView>({fov: 1.25, pitch: 0, yaw: 0});
  const frameRef = useRef<number | null>(null);
  const lastTimeRef = useRef(0);
  const autoRef = useRef(true);
  const pointersRef = useRef(new Map<number, {x: number; y: number}>());
  const pinchRef = useRef<{distance: number; fov: number} | null>(null);

  const [status, setStatus] = useState<'loading' | 'ready' | 'fallback'>('loading');
  const [dragging, setDragging] = useState(false);
  const [touched, setTouched] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  /** Draw one frame; keep drawing only while the view is still drifting. */
  const render = useCallback(() => {
    frameRef.current = null;
    const renderer = rendererRef.current;
    if (!renderer) return;

    const now = performance.now();
    const delta = lastTimeRef.current ? Math.min(0.05, (now - lastTimeRef.current) / 1000) : 0;
    lastTimeRef.current = now;

    if (autoRef.current) viewRef.current.yaw += AUTO_SPEED * delta;
    renderer.draw(viewRef.current);

    if (autoRef.current) frameRef.current = requestAnimationFrame(render);
  }, []);

  const invalidate = useCallback(() => {
    if (frameRef.current === null) frameRef.current = requestAnimationFrame(render);
  }, [render]);

  const stopAuto = useCallback(() => {
    autoRef.current = false;
    setTouched(true);
  }, []);

  /* -------------------------------------------------------------- lifecycle */

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    /* The canvas is created here, per mount, instead of living in the JSX.
       A WebGL context cannot be un-lost: `destroy()` calls `loseContext()` to
       free the drawing buffer (browsers keep only a handful alive, and a
       gallery opens many tours), and `getContext` on that same element
       afterwards hands back the SAME dead context. React StrictMode mounts
       every effect twice in development — mount, clean up, mount again — so a
       canvas written in JSX would be permanently dead on the second mount and
       every viewer would silently fall back to a flat image. A fresh element
       per mount is the only version that is correct in both modes. */
    const canvas = document.createElement('canvas');
    canvas.className = 'block h-full w-full';
    // Keep it out of the genie's rasterised snapshot: a WebGL drawing buffer
    // without `preserveDrawingBuffer` reads back empty, so including it would
    // funnel a black rectangle where the tour is. The stage behind it still
    // rasterises, so the shape of the app is intact.
    canvas.setAttribute('data-genie-ignore', '');
    host.prepend(canvas);
    canvasRef.current = canvas;

    const renderer = createRenderer(canvas);
    if (!renderer) {
      canvas.remove();
      canvasRef.current = null;
      setStatus('fallback');
      return;
    }
    rendererRef.current = renderer;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    autoRef.current = !reduced;
    viewRef.current = {fov: 1.25, pitch: 0, yaw: 0};
    lastTimeRef.current = 0;

    let alive = true;
    const image = new Image();
    // Storage URLs are cross-origin; without this the texture upload throws.
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      if (!alive) return;
      renderer.setImage(image);
      setStatus('ready');
      invalidate();
    };
    image.onerror = () => {
      if (alive) setStatus('fallback');
    };
    image.src = src;

    const onResize = () => {
      renderer.resize();
      invalidate();
    };
    window.addEventListener('resize', onResize);

    return () => {
      alive = false;
      window.removeEventListener('resize', onResize);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      rendererRef.current = null;
      renderer.destroy();
      canvas.remove();
      canvasRef.current = null;
    };
  }, [invalidate, src]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === hostRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Full screen changes the canvas box without firing `resize` in every browser.
  useEffect(() => {
    rendererRef.current?.resize();
    invalidate();
  }, [fullscreen, invalidate]);

  /* --------------------------------------------------------------- gestures */

  const look = useCallback(
    (dx: number, dy: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Move by the ANGLE the pixel covers, so a drag feels the same at every
      // zoom level: dragging 100px always turns the same part of the room.
      const perPixel = viewRef.current.fov / Math.max(1, canvas.clientHeight);
      viewRef.current.yaw -= dx * perPixel;
      viewRef.current.pitch = clamp(viewRef.current.pitch + dy * perPixel, -MAX_PITCH, MAX_PITCH);
      invalidate();
    },
    [invalidate],
  );

  const zoom = useCallback(
    (factor: number) => {
      viewRef.current.fov = clamp(viewRef.current.fov * factor, MIN_FOV, MAX_FOV);
      invalidate();
    },
    [invalidate],
  );

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (status !== 'ready') return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, {x: event.clientX, y: event.clientY});
    stopAuto();
    if (pointersRef.current.size === 1) setDragging(true);
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = {
        distance: Math.hypot(a!.x - b!.x, a!.y - b!.y),
        fov: viewRef.current.fov,
      };
    }
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const pointers = pointersRef.current;
    const previous = pointers.get(event.pointerId);
    if (!previous) return;
    pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});

    if (pointers.size >= 2 && pinchRef.current) {
      const [a, b] = [...pointers.values()];
      const distance = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      if (distance > 0) {
        viewRef.current.fov = clamp(
          (pinchRef.current.fov * pinchRef.current.distance) / distance,
          MIN_FOV,
          MAX_FOV,
        );
        invalidate();
      }
      return;
    }

    look(event.clientX - previous.x, event.clientY - previous.y);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) setDragging(false);
  };

  const onWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (status !== 'ready') return;
    stopAuto();
    zoom(event.deltaY > 0 ? 1.1 : 0.9);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = 40;
    const keys: Record<string, () => void> = {
      '-': () => zoom(1.12),
      '+': () => zoom(0.89),
      '=': () => zoom(0.89),
      ArrowDown: () => look(0, -step),
      ArrowLeft: () => look(step, 0),
      ArrowRight: () => look(-step, 0),
      ArrowUp: () => look(0, step),
    };
    const action = keys[event.key];
    if (!action) return;
    event.preventDefault();
    stopAuto();
    action();
  };

  const recentre = () => {
    stopAuto();
    viewRef.current = {fov: 1.25, pitch: 0, yaw: 0};
    invalidate();
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void hostRef.current?.requestFullscreen?.();
  };

  /* ----------------------------------------------------------------- render */

  if (status === 'fallback') {
    // No WebGL (or the image refused to load): show the panorama as what it is
    // — a very wide picture — instead of an error the visitor cannot act on.
    return (
      <div className="pano-stage relative overflow-x-auto rounded-card" style={{cursor: 'auto'}}>
        <img src={src} alt={label ?? 'Ảnh 360'} className="h-full max-w-none object-cover" />
        <p className="photo-tag absolute bottom-3 left-3 rounded-full px-3 py-1.5 text-[11px] font-semibold">
          Trình duyệt chưa hỗ trợ 3D — cuộn ngang để xem
        </p>
      </div>
    );
  }

  return (
    <div
      ref={hostRef}
      className="pano-stage relative h-full w-full overflow-hidden rounded-card"
      data-dragging={dragging}
      tabIndex={0}
      role="application"
      aria-label={`Ảnh 360 độ${label ? `: ${label}` : ''}`}
      onKeyDown={onKeyDown}
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {status === 'loading' ? (
        <div className="absolute inset-0 flex items-center justify-center text-[12px] font-semibold text-white/80">
          Đang dựng ảnh 360…
        </div>
      ) : null}

      {label ? (
        <p className="photo-tag pointer-events-none absolute left-3 top-3 rounded-full px-3 py-1.5 text-[11px] font-semibold">
          {label}
        </p>
      ) : null}

      {status === 'ready' && !touched ? (
        <p className="photo-tag pointer-events-none absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold">
          <Compass size={14} /> Kéo để nhìn quanh
        </p>
      ) : null}

      <div className="absolute right-3 top-3 flex flex-col gap-2">
        <PanoButton label="Phóng to" onClick={() => {stopAuto(); zoom(0.85);}}>
          <ZoomIn size={16} />
        </PanoButton>
        <PanoButton label="Thu nhỏ" onClick={() => {stopAuto(); zoom(1.18);}}>
          <ZoomOut size={16} />
        </PanoButton>
        <PanoButton label="Về góc nhìn ban đầu" onClick={recentre}>
          <RotateCcw size={16} />
        </PanoButton>
        <PanoButton
          label={fullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
          onClick={toggleFullscreen}
        >
          {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </PanoButton>
      </div>
    </div>
  );
}

function PanoButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      // The stage owns the pointer for looking around; a control inside it must
      // not start a drag on its way to being clicked.
      onPointerDown={(event) => event.stopPropagation()}
      className="photo-tag flex h-9 w-9 items-center justify-center rounded-full transition-transform active:scale-95"
    >
      {children}
    </button>
  );
}
